'use client';

import { c as _c } from "react/compiler-runtime";
import { PictureInPicture2Icon, RotateCcwIcon, SmartphoneIcon, TabletSmartphoneIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { Fragment, type ReactNode } from 'react';
import type { PlayLaunchMode, PlayOrientation as PlayOrientationType } from '@/editor/core/play-preview/playPreviewTypes';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { InputNumber } from '@/components/ui/input-number';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PLAY_DEVICE_PRESETS, PLAY_WINDOW_SIZE_PRESETS, PlayLaunchMode as PlayLaunchModeValues, PlayOrientation } from '@/editor/core/play-preview/playPreviewTypes';
import { store } from '@/editor/store';
import { cn } from '@/lib/utils';
interface PlayPreviewSettingsDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onStartPlay: (mode: PlayLaunchMode) => void;
}
const ORIENTATION_OPTIONS: Array<{
  value: PlayOrientationType;
  label: string;
}> = [{
  value: PlayOrientation.Portrait,
  label: 'Portrait'
}, {
  value: PlayOrientation.Landscape,
  label: 'Landscape'
}];
const CUSTOM_WINDOW_PRESET_ID = 'custom';
const WINDOW_PRESET_GROUPS: Array<{
  key: 'desktop' | 'laptop' | 'tablet' | 'phone';
  label: string;
}> = [{
  key: 'desktop',
  label: 'Desktop'
}, {
  key: 'laptop',
  label: 'Laptop'
}, {
  key: 'tablet',
  label: 'Tablet'
}, {
  key: 'phone',
  label: 'Phone'
}];
export const PlayPreviewSettingsDialog = observer(({
  isOpen,
  onOpenChange,
  onStartPlay
}: PlayPreviewSettingsDialogProps) => {
  const groupedWindowPresets = {
    desktop: PLAY_WINDOW_SIZE_PRESETS.filter(preset => preset.kind === 'desktop'),
    laptop: PLAY_WINDOW_SIZE_PRESETS.filter(preset => preset.kind === 'laptop'),
    tablet: PLAY_WINDOW_SIZE_PRESETS.filter(preset => preset.kind === 'tablet'),
    phone: PLAY_WINDOW_SIZE_PRESETS.filter(preset => preset.kind === 'phone')
  };
  const groupedMobilePresets = {
    phone: PLAY_DEVICE_PRESETS.filter(preset => preset.kind === 'phone'),
    tablet: PLAY_DEVICE_PRESETS.filter(preset => preset.kind === 'tablet')
  };
  const selectedWindowPresetId = PLAY_WINDOW_SIZE_PRESETS.find(preset => preset.width === store.playWindowWidth && preset.height === store.playWindowHeight)?.id ?? CUSTOM_WINDOW_PRESET_ID;
  const handleWindowPresetChange = (value: string) => {
    if (value === CUSTOM_WINDOW_PRESET_ID) {
      return;
    }
    const preset = PLAY_WINDOW_SIZE_PRESETS.find(item => item.id === value);
    if (!preset) {
      return;
    }
    store.setPlayWindowSize(preset.width, preset.height);
  };
  return <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[min(900px,calc(100%-2rem))] p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>Play Mode Settings</DialogTitle>
          <DialogDescription className="mb-0">
            Configure New Window and Mobile Preview launch settings for Play Mode.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-5 px-6 py-2 lg:grid-cols-2">
          <section className="space-y-4 rounded-md border border-border/70 bg-input/10 p-4">
            <h3 className="text-xs font-semibold text-secondary-foreground">Play in New Window</h3>
            <div className="space-y-1">
              <LabelWithTooltip htmlFor="play-window-size-preset" label="Size Preset" description="Quickly apply common desktop, laptop, tablet, and phone viewport sizes. Use Custom to set your own width and height." />
              <Select value={selectedWindowPresetId} onValueChange={handleWindowPresetChange}>
                <SelectTrigger id="play-window-size-preset" className="w-full">
                  <SelectValue placeholder="Select size preset" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CUSTOM_WINDOW_PRESET_ID}>Custom</SelectItem>
                  <SelectSeparator />
                  {WINDOW_PRESET_GROUPS.map((group, index) => <Fragment key={group.key}>
                      {index > 0 ? <SelectSeparator /> : null}
                      <SelectGroup>
                        <SelectLabel>{group.label}</SelectLabel>
                        {groupedWindowPresets[group.key].map(preset => <SelectItem key={preset.id} value={preset.id}>{preset.name}</SelectItem>)}
                      </SelectGroup>
                    </Fragment>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <LabelWithTooltip htmlFor="play-window-width" label="Width" description="Window content width in pixels for Play in New Window." />
                <InputNumber id="play-window-width" value={store.playWindowWidth} min={240} step={1} decimalPlaces={0} onChange={value => store.setPlayWindowSize(value, store.playWindowHeight)} />
              </div>
              <div className="space-y-1">
                <LabelWithTooltip htmlFor="play-window-height" label="Height" description="Window content height in pixels for Play in New Window." />
                <InputNumber id="play-window-height" value={store.playWindowHeight} min={240} step={1} decimalPlaces={0} onChange={value => store.setPlayWindowSize(store.playWindowWidth, value)} />
              </div>
            </div>
            <div className="space-y-1">
              <LabelWithTooltip htmlFor="play-window-dpr" label="DPR Override" description="Device Pixel Ratio override for rendering sharpness. Higher values increase visual quality and GPU load." />
              <InputNumber id="play-window-dpr" value={store.playWindowDevicePixelRatio} min={0.5} max={4} step={0.25} decimalPlaces={2} onChange={value => store.setPlayWindowDevicePixelRatio(value)} />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <LabelWithTooltip htmlFor="play-window-center" label="Center Window" description="Open the new play window centered on the active display." />
                <Switch id="play-window-center" checked={store.playWindowCenter} onCheckedChange={value => store.setPlayWindowCentered(value)} />
              </div>
              <div className="flex items-center justify-between">
                <LabelWithTooltip htmlFor="play-window-resizable" label="Resizable Window" description="Allow resizing the play window while the game is running." />
                <Switch id="play-window-resizable" checked={store.playWindowResizable} onCheckedChange={value => store.setPlayWindowResizable(value)} />
              </div>
            </div>
          </section>

          <section className="space-y-4 rounded-md border border-border/70 bg-input/10 p-4">
            <h3 className="text-xs font-semibold text-secondary-foreground">Mobile Preview</h3>
            <div className="space-y-1">
              <LabelWithTooltip htmlFor="play-mobile-device-preset" label="Device Preset" description="Choose a phone or tablet preset. Presets apply recommended viewport size, DPR, safe area, and interaction defaults." />
              <Select value={store.playPreviewPresetId} onValueChange={value => store.setPlayDevicePreset(value)}>
                <SelectTrigger id="play-mobile-device-preset" className="w-full">
                  <SelectValue placeholder="Select a preset" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Phones</SelectLabel>
                    {groupedMobilePresets.phone.map(preset => <SelectItem key={preset.id} value={preset.id}>{preset.name}</SelectItem>)}
                  </SelectGroup>
                  <SelectSeparator />
                  <SelectGroup>
                    <SelectLabel>Tablets</SelectLabel>
                    {groupedMobilePresets.tablet.map(preset => <SelectItem key={preset.id} value={preset.id}>{preset.name}</SelectItem>)}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <LabelWithTooltip label="Orientation" description="Switch between portrait and landscape orientations for the selected mobile device." />
              <div className="flex gap-2">
                {ORIENTATION_OPTIONS.map(option => <Button key={option.value} size="sm" data-state={store.playPreviewOrientation === option.value ? 'on' : 'off'} onClick={() => store.setPlayOrientation(option.value)} className="flex-1">
                    {option.value === PlayOrientation.Portrait ? <SmartphoneIcon className="size-3.5" /> : <TabletSmartphoneIcon className="size-3.5" />}
                    {option.label}
                  </Button>)}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <LabelWithTooltip htmlFor="play-mobile-width" label="Width" description="Mobile preview viewport width in CSS pixels." />
                <InputNumber id="play-mobile-width" value={store.playPreviewWidth} min={120} step={1} decimalPlaces={0} onChange={value => store.setPlayPreviewCustomSize(value, store.playPreviewHeight)} />
              </div>
              <div className="space-y-1">
                <LabelWithTooltip htmlFor="play-mobile-height" label="Height" description="Mobile preview viewport height in CSS pixels." />
                <InputNumber id="play-mobile-height" value={store.playPreviewHeight} min={120} step={1} decimalPlaces={0} onChange={value => store.setPlayPreviewCustomSize(store.playPreviewWidth, value)} />
              </div>
              <div className="space-y-1">
                <LabelWithTooltip htmlFor="play-mobile-dpr" label="DPR" description="Device Pixel Ratio used for mobile preview rendering scale." />
                <InputNumber id="play-mobile-dpr" value={store.playPreviewDevicePixelRatio} min={0.5} max={4} step={0.25} decimalPlaces={2} onChange={value => store.setPlayDevicePixelRatio(value)} />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1">
                <LabelWithTooltip htmlFor="play-mobile-safe-top" label="Safe Top" description="Top inset reserved for system UI areas like camera cutouts." />
                <InputNumber id="play-mobile-safe-top" value={store.playPreviewSafeAreaTop} min={0} step={1} decimalPlaces={0} onChange={value => store.setPlaySafeAreaInset('top', value)} />
              </div>
              <div className="space-y-1">
                <LabelWithTooltip htmlFor="play-mobile-safe-right" label="Safe Right" description="Right-side safe area inset." />
                <InputNumber id="play-mobile-safe-right" value={store.playPreviewSafeAreaRight} min={0} step={1} decimalPlaces={0} onChange={value => store.setPlaySafeAreaInset('right', value)} />
              </div>
              <div className="space-y-1">
                <LabelWithTooltip htmlFor="play-mobile-safe-bottom" label="Safe Bottom" description="Bottom inset reserved for navigation/home indicators." />
                <InputNumber id="play-mobile-safe-bottom" value={store.playPreviewSafeAreaBottom} min={0} step={1} decimalPlaces={0} onChange={value => store.setPlaySafeAreaInset('bottom', value)} />
              </div>
              <div className="space-y-1">
                <LabelWithTooltip htmlFor="play-mobile-safe-left" label="Safe Left" description="Left-side safe area inset." />
                <InputNumber id="play-mobile-safe-left" value={store.playPreviewSafeAreaLeft} min={0} step={1} decimalPlaces={0} onChange={value => store.setPlaySafeAreaInset('left', value)} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center justify-between">
                <LabelWithTooltip htmlFor="play-mobile-viewport" label="Mobile Viewport" description="Enable mobile viewport behavior for layout and viewport emulation." />
                <Switch id="play-mobile-viewport" checked={store.playPreviewMobileViewport} onCheckedChange={value => store.setPlayMobileViewport(value)} />
              </div>
              <div className="flex items-center justify-between">
                <LabelWithTooltip htmlFor="play-touch" label="Touch Emulation" description="Emit touch input events to simulate tapping and gestures." />
                <Switch id="play-touch" checked={store.playPreviewTouchEmulation} onCheckedChange={value => store.toggleTouchEmulation(value)} />
              </div>
              <div className="flex items-center justify-between">
                <LabelWithTooltip htmlFor="play-device-frame" label="Device Frame" description="Draw a phone/tablet frame around the mobile preview viewport." />
                <Switch id="play-device-frame" checked={store.playPreviewShowDeviceFrame} onCheckedChange={value => store.toggleDeviceFrame(value)} />
              </div>
              <div className="flex items-center justify-between">
                <LabelWithTooltip htmlFor="play-notch-overlay" label="Notch Overlay" description="Overlay a notch or camera area visual over the preview when applicable." />
                <Switch id="play-notch-overlay" checked={store.playPreviewShowNotchOverlay} onCheckedChange={value => store.toggleNotchOverlay(value)} />
              </div>
            </div>
          </section>
        </div>

        <DialogFooter className="px-6 pb-6 pt-0 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Button onClick={() => store.resetPlayPreviewSettings()}>
            <RotateCcwIcon className="size-4" />
            Reset
          </Button>
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-2">
            <Button variant="primaryHover" className="gap-2" onClick={() => onStartPlay(PlayLaunchModeValues.NewWindow)}>
              <PictureInPicture2Icon className="size-4 shrink-0" />
              Play in New Window
            </Button>
            <Button variant="primaryHover" className="gap-2" onClick={() => onStartPlay(PlayLaunchModeValues.MobilePreview)}>
              <SmartphoneIcon className="size-4 shrink-0" />
              Play in Mobile Preview
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
});
PlayPreviewSettingsDialog.displayName = 'PlayPreviewSettingsDialog';
interface LabelWithTooltipProps {
  label: string;
  description: ReactNode;
  htmlFor?: string;
}
const LabelWithTooltip = t0 => {
  const $ = _c(11);
  const {
    label,
    description,
    htmlFor
  } = t0;
  const t1 = htmlFor && "peer-disabled:cursor-not-allowed";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("w-fit cursor-help", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== htmlFor || $[3] !== label || $[4] !== t2) {
    t3 = <TooltipTrigger asChild={true}><Label htmlFor={htmlFor} className={t2}>{label}</Label></TooltipTrigger>;
    $[2] = htmlFor;
    $[3] = label;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== description) {
    t4 = <TooltipContent side="top" className="max-w-72 text-xs">{description}</TooltipContent>;
    $[6] = description;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <Tooltip delayDuration={400}>{t3}{t4}</Tooltip>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
LabelWithTooltip.displayName = 'LabelWithTooltip';